import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { dramas } from "@/mocks/dramas";
import { actors } from "@/mocks/actors";
import { articles } from "@/mocks/articles";
import { getDramaById } from "@/lib/content";
import HeroSection from "./components/HeroSection";
import DramaCard from "@/components/feature/DramaCard";
import ActorCard from "@/components/feature/ActorCard";
import { MAIN_DRAMA_ID } from "@/components/feature/navItems";

const entries = [
  {
    icon: "ri-book-open-line",
    title: "剧情简介",
    desc: "完整剧情梗概与分集解析，快速掌握故事脉络。",
    to: `/detail/${MAIN_DRAMA_ID}`,
  },
  {
    icon: "ri-user-star-line",
    title: "演员介绍",
    desc: "主演资料与代表作品，了解台前幕后的故事。",
    to: "/category?tab=actor",
  },
  {
    icon: "ri-chat-quote-line",
    title: "剧集点评",
    desc: "真实观众评分与点评，观剧前先看看大家怎么说。",
    to: `/reviews/${MAIN_DRAMA_ID}`,
  },
];

export default function Home() {
  useSeo({
    title: "风流女管家 - 影视剧介绍与剧情解析",
    description:
      "风流女管家网站提供丰富的影视剧资讯，包括剧情介绍、演员信息和剧集点评，帮助用户全面了解该剧内容。",
    keywords: "风流女管家,影视剧,剧情介绍,演员表,剧集点评",
  });

  const mainDrama = getDramaById(MAIN_DRAMA_ID);
  const latestArticles = [...articles]
    .sort((a, b) => (a.date < b.date ? 1 : -1))
    .slice(0, 5);
  const hotActors = actors.slice(0, 6);
  const otherDramas = dramas.filter((d) => d.id !== MAIN_DRAMA_ID);

  return (
    <div className="animate-fade-in">
      {mainDrama && <HeroSection drama={mainDrama} />}

      <div className="mx-auto w-full max-w-6xl px-4 md:px-6">
        {/* 分类入口 */}
        <section className="-mt-10 relative z-20 grid grid-cols-1 gap-4 md:-mt-12 sm:grid-cols-3">
          {entries.map((entry) => (
            <Link
              key={entry.title}
              to={entry.to}
              className="group flex cursor-pointer flex-col rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300"
            >
              <span className="mb-3 flex h-11 w-11 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                <i className={`${entry.icon} text-xl`} />
              </span>
              <h2 className="text-base font-semibold text-foreground-900 transition-colors group-hover:text-primary-600">
                {entry.title}
              </h2>
              <p className="mt-1.5 text-sm leading-relaxed text-foreground-500">
                {entry.desc}
              </p>
            </Link>
          ))}
        </section>

        {/* 最新剧情解析 */}
        <section className="mt-16">
          <div className="mb-6 flex items-end justify-between">
            <div>
              <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                最新剧情解析
              </h2>
              <p className="mt-1 text-sm text-foreground-500">
                深度拆解剧情细节与人物动机
              </p>
            </div>
          </div>
          <div className="space-y-4">
            {latestArticles.map((item) => {
              const related = getDramaById(item.dramaId);
              return (
                <article
                  key={item.id}
                  className="group flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-200 sm:flex-row"
                >
                  <span className="block h-40 w-full shrink-0 overflow-hidden rounded-md bg-background-200 sm:h-28 sm:w-48">
                    <img
                      src={item.cover}
                      alt={item.title}
                      title={`${item.title} 配图`}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
                      <span className="rounded-full bg-accent-100 px-2 py-0.5 font-medium text-accent-800">
                        {item.category}
                      </span>
                      <span>{item.date}</span>
                      <span className="text-foreground-300">·</span>
                      <span>阅读约 {item.readTime}</span>
                    </div>
                    <h3 className="mt-2 text-base font-semibold text-foreground-900 transition-colors group-hover:text-primary-600 md:text-lg">
                      {item.title}
                    </h3>
                    <p className="mt-1.5 line-clamp-2 text-sm leading-relaxed text-foreground-500">
                      {item.summary}
                    </p>
                    {related && (
                      <Link
                        to={`/detail/${related.id}`}
                        className="mt-2 inline-flex cursor-pointer items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
                      >
                        相关剧集：{related.title}
                        <i className="ri-arrow-right-s-line" />
                      </Link>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        {/* 热门演员 */}
        <section className="mt-16">
          <div className="mb-6 flex items-end justify-between">
            <div>
              <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                热门演员
              </h2>
              <p className="mt-1 text-sm text-foreground-500">
                点击卡片查看完整演员资料
              </p>
            </div>
            <Link
              to="/category?tab=actor"
              className="hidden items-center gap-1 text-sm text-primary-600 hover:text-primary-700 sm:inline-flex"
            >
              查看全部
              <i className="ri-arrow-right-s-line" />
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
            {hotActors.map((actor) => (
              <ActorCard key={actor.id} actor={actor} />
            ))}
          </div>
        </section>

        {/* 精彩剧集 */}
        <section className="mt-16">
          <div className="mb-6 flex items-end justify-between">
            <div>
              <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                精彩剧集推荐
              </h2>
              <p className="mt-1 text-sm text-foreground-500">
                同题材优质作品，一起追起来
              </p>
            </div>
            <Link
              to="/category"
              className="hidden items-center gap-1 text-sm text-primary-600 hover:text-primary-700 sm:inline-flex"
            >
              进入分类页
              <i className="ri-arrow-right-s-line" />
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {otherDramas.map((drama) => (
              <DramaCard key={drama.id} drama={drama} />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}